import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Reveal } from "./motion-primitives";
import { SectionLabel } from "./About";
import { useLang, Rich } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONTACT_BG = "/images/osrelis-6.jpg";

const EMPTY = { name: "", email: "", company: "", message: "" };

const Field = ({ label, name, value, onChange, type = "text", textarea = false, required = false }) => (
  <label className="block">
    <span className="mb-2 block font-sans text-[10px] uppercase tracking-[0.16em] text-paper/45">{label}</span>
    {textarea ? (
      <textarea
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        rows={4}
        data-testid={`contact-${name}`}
        className="w-full resize-none border-b border-paper/25 bg-transparent pb-3 font-sans text-[15px] text-paper outline-none transition-colors duration-300 placeholder:text-paper/25 focus:border-teal"
      />
    ) : (
      <input
        name={name}
        value={value}
        onChange={onChange}
        type={type}
        required={required}
        data-testid={`contact-${name}`}
        className="w-full border-b border-paper/25 bg-transparent pb-3 font-sans text-[15px] text-paper outline-none transition-colors duration-300 placeholder:text-paper/25 focus:border-teal"
      />
    )}
  </label>
);

export const Contact = () => {
  const { c } = useLang();
  const t = c.contact;
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
      });
      toast.success(t.form.success);
      setForm(EMPTY);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t.form.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="section-contact" className="relative flex min-h-[780px] items-center overflow-hidden bg-deep text-paper">
      <div className="absolute inset-0">
        <img src={CONTACT_BG} alt="Offshore platform with gas flare over open sea" className="h-full w-full object-cover" style={{ filter: "saturate(0.6)" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, rgba(6,18,21,.98), rgba(6,18,21,.82) 55%, rgba(6,18,21,.74))" }} />
      </div>

      <div className="relative z-[1] grid w-full gap-14 px-6 py-24 md:grid-cols-[1fr_0.9fr] md:gap-[8vw] md:px-[8vw] md:py-28">
        <div>
          <Reveal>
            <SectionLabel n="08" onDark>{t.label}</SectionLabel>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-serif text-[clamp(44px,6vw,96px)] font-normal leading-[0.98] tracking-[-0.02em]">
              <Rich text={t.title} />
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mb-12 mt-8 max-w-[520px] font-sans leading-[1.7] text-paper/65">{t.intro}</p>
          </Reveal>
          <Reveal delay={0.24}>
            <a
              href="mailto:CONTACT@osrelis.com"
              data-testid="contact-email-link"
              className="flex w-full max-w-[560px] items-center justify-between border-b border-paper/35 pb-3.5 font-serif text-[clamp(24px,3vw,42px)] text-paper transition-colors duration-300 hover:border-teal hover:text-teal"
            >
              CONTACT@osrelis.com <span>↗</span>
            </a>
            <div className="mt-14 flex flex-col gap-2 md:flex-row md:items-baseline md:gap-7">
              <span className="font-sans text-[9px] uppercase tracking-[0.12em] text-paper/45">{t.hqPrefix}</span>
              <strong className="font-sans text-xs font-medium">{t.hqPlace}</strong>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.2}>
          <form onSubmit={onSubmit} data-testid="contact-form" className="flex flex-col gap-7 border border-paper/10 bg-deep/50 p-7 backdrop-blur-md md:p-9">
            <Field label={t.form.name} name="name" value={form.name} onChange={onChange} required />
            <Field label={t.form.email} name="email" type="email" value={form.email} onChange={onChange} required />
            <Field label={t.form.company} name="company" value={form.company} onChange={onChange} />
            <Field label={t.form.message} name="message" value={form.message} onChange={onChange} textarea required />
            <button
              type="submit"
              disabled={sending}
              data-testid="contact-submit"
              className="group mt-2 inline-flex items-center justify-between gap-6 bg-paper px-5 py-4 font-sans text-xs tracking-[0.04em] text-ink transition-colors duration-300 hover:bg-teal disabled:opacity-60"
            >
              {sending ? t.form.sending : t.form.submit}
              {sending ? <Loader2 size={15} className="animate-spin" /> : <span className="transition-transform duration-300 group-hover:translate-x-1">→</span>}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
